import React from "react"
import SearchFilter from '@/forms/_fields_v2/SearchFilter';
import {BuildingLibraryIcon, BuildingOfficeIcon} from "@heroicons/react/24/outline";
import {useEffect, useMemo, useState} from 'react';
import {debounce} from 'lodash';
import {AnimatePresence, motion} from "framer-motion";
import {useTranslation} from "react-i18next";
import SiteDetails from "@/pages/workspaces/sales/SiteDetails";
import {useSyncSitesToIndexedDb} from "@/hooks/useSyncSitesToIndexedDb";
import useCoreSiteLeads from "@/hooks/useCoreSiteLeads";
import {useMap} from "@vis.gl/react-google-maps";
import {useSales} from "@/pages/workspaces/sales/SalesContext";
import titleCase from "@/utils/titleCase";
import EducatorContacts from "@/pages/workspaces/sales/EducatorContacts";
import SiteHealth from "@/pages/workspaces/sales/SiteHealth";
import PropTypes from 'prop-types';

const SITE_STYLES = {
  'network/operational': {
    icon: 'bg-cs-orange-600 text-white',
  },
  'network/on_hold': {
    icon: 'text-cs-gray-800',
  },
  'single/operational': {
    icon: 'bg-cs-blue-600 text-white',
  },
  'single/on_hold': {
    icon: 'bg-cs-gray-800 text-white',
  }
}
SiteIcon.propTypes = {
  site: PropTypes.shape({
    networkCount: PropTypes.number,
    isActive: PropTypes.bool,
  }),
  className: PropTypes.string,
}
function SiteIcon({site, className}) {
  const siteKind = site?.networkCount > 1 ? 'network' : 'single'
  const siteFunding = site?.isActive ? 'operational' : 'on_hold'
  const {icon: iconClasses} = SITE_STYLES[`${siteKind}/${siteFunding}`]

  return (
    <div className={`rounded-full w-6 h-6 flex items-center justify-center flex-none ${iconClasses}`}>
      {site.networkCount > 1 && <BuildingOfficeIcon className={className}/>}
      {site.networkCount <= 1 && <BuildingLibraryIcon className={className}/>}
    </div>
  )
}

export default function SiteSidebar() {
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');

  const {t} = useTranslation();
  const {allSites} = useSyncSitesToIndexedDb();
  const {data: leadsData, isLoading: isLeadsLoading} = useCoreSiteLeads({pageSize: 200, q: searchQuery || undefined});
  const map = useMap();
  const {dispatch} = useSales();

  const debouncedSearch = useMemo(
    () => debounce((value) => setSearchQuery(value), 300),
    []
  );

  const handleSearchChange = (value) => {
    setInputValue(value);
    dispatch({type: 'CLEAR_CLICKED_ITEM'});
    debouncedSearch(value);
  };

  // Cleanup debounce on unmounting
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const siteMap = useMemo(() => new Map(allSites.map(s => [s.id, s])), [allSites]);

  const filteredLeads = useMemo(() => {
    if (isLeadsLoading || !leadsData?.data) {
      return [];
    }

    // Augment leads data with full site details from allSites
    const augmentedLeads = leadsData.data.map(lead => {
      const fullSite = siteMap.get(lead.id);
      return {
        ...lead,
        // Add properties from full site data
        city: fullSite?.city,
        state: fullSite?.state,
        addressLine1: fullSite?.addressLine1,
        latitude: fullSite?.latitude,
        longitude: fullSite?.longitude,
        networkCount: fullSite?.networkCount,
        isActive: lead.isActive !== undefined ? lead.isActive : fullSite?.isActive,
        name: lead.name || fullSite?.name
      };
    });

    return augmentedLeads;
  }, [leadsData, allSites, searchQuery, isLeadsLoading]);


  const handleClick = (site) => {
    if (!site) return;
    const feature = {
      id: site.id,
      lat: Number(site.latitude),
      lng: Number(site.longitude),
      cluster: false,
      point_count: 0,
      name: site.name,
      isActive: site.isActive,
      properties: {...site}
    };
    dispatch({type: 'SET_CLICKED_ITEM', payload: feature});
    // https://communityshare.sentry.io/issues/7238677722/events/68bc93bd48694b3a99196638574e8866/
    if (map && Number.isFinite(feature.lat) && Number.isFinite(feature.lng)) {
      const zoom = map.getZoom()
      map.panTo({lat: feature.lat, lng: feature.lng});
      if (zoom < 12) map.setZoom(12);
    }
  };

  return (
    <div>
      <aside className="w-full flex flex-col gap-4">
        {/* Search */}
        <SearchFilter
          value={inputValue}
          onChange={handleSearchChange}
        />

        <SiteDetails/>

        <EducatorContacts/>

        <SiteHealth/>

        {/* Potential leads */}
        <section>
          <div className="rounded-lg p-4 bg-base-200">
            <h3 className="text-base font-semibold text-gray-700">
              {t('workspace.sales.siteSidebar.potential_leads')}
            </h3>
            <p className="mt-1 text-base text-gray-700">
              {t('workspace.sales.siteSidebar.recent_educators')}

            </p>

            <ul className="mt-3 space-y-3 h-96 overflow-scroll">
              <AnimatePresence>
                {filteredLeads.map((site) => (
                  <motion.li key={site.id}
                             layout
                             initial={{opacity: 0, y: 8}}
                             animate={{opacity: 1, y: 0}}
                             exit={{opacity: 0, y: -8}}
                             transition={{duration: 0.18}}
                             className="flex gap-2 items-start">
                    <SiteIcon site={site} className="w-4 h-4"/>
                    <div>
                      <button className="text-base font-semibold text-base-content underline text-left"
                              onClick={() => handleClick(site)}>
                        {site.name}
                      </button>
                      <p className="text-xs text-gray-700">
                        {titleCase(site.addressLine1)}
                        <br/>
                        {titleCase(site.city)}, {site.state}
                      </p>
                    </div>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          </div>
        </section>
      </aside>
    </div>
  );
};

